// src/pages/orders/OrderDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Wallet,
  Package,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  CreditCard,
  Store,
  Truck,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Activity,
  History,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Loader2,
  Box,
  RotateCcw,
  Receipt,
  Share2
} from 'lucide-react';
import { 
  getEcomOrder, 
  getOrderAuditHistory, 
  getOrderAuditSummary, 
  getOrderCourierTracking,
  getOrderRelations 
} from '../../services/api/orders';
import { useLocalization } from '../../contexts/LocalizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { reverseSingleOrder } from '../../services/api/reversal';
import ReversalConfirmDialog from '../../components/common/ReversalConfirmDialog/ReversalConfirmDialog';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { PaymentMethodIcon } from '../../utils/paymentMethodIcons';
import { useAlert } from '../../contexts/AlertContext';
import EcomOrderPaymentRecordsModal from './components/EcomOrderPaymentRecordsModal';
import styles from './OrderDetailsPage.module.css';
import BundleItemGroup from '../../components/orders/BundleItemGroup/BundleItemGroup';
import OrderShiftLink from '../../components/orders/OrderShiftLink/OrderShiftLink';
import OrderOriginBadge from '../../components/orders/OrderOriginBadge/OrderOriginBadge';
import { groupOrderItems } from '../../utils/orderItemGrouping';

const AUDIT_ITEM_FIELDS = ['quantity', 'unitPrice', 'lineDiscount', 'lineTotal'];
const AUDIT_ITEM_GROUPS = [
  { key: 'updated', labelKey: 'ecomOrders.auditItemUpdated' },
  { key: 'added', labelKey: 'ecomOrders.auditItemAdded' },
  { key: 'removed', labelKey: 'ecomOrders.auditItemRemoved' },
];

const OrderDetailsPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const { success, error: showError } = useAlert();

  const [order, setOrder] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [courierTracking, setCourierTracking] = useState(null);
  const [orderRelations, setOrderRelations] = useState(null);
  
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [isLoadingRelations, setIsLoadingRelations] = useState(false);
  
  const [orderError, setOrderError] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [trackingError, setTrackingError] = useState(null);
  const [relationsError, setRelationsError] = useState(null);

  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [showCourierTracking, setShowCourierTracking] = useState(false);

  const [auditPage, setAuditPage] = useState(1);
  const [auditLimit] = useState(10);
  const [auditTotal, setAuditTotal] = useState(0);

  const canReverse = hasPermission('reverse_packing_batch');
  const [showReverseDialog, setShowReverseDialog] = useState(false);
  const [isReversing, setIsReversing] = useState(false);

  // Load order details
  useEffect(() => {
    loadOrderDetails();
    loadOrderRelations();
  }, [orderId]);

  // Load audit summary on mount
  useEffect(() => {
    if (orderId) {
      loadAuditSummary();
    }
  }, [orderId]);

  // Load audit history when expanded
  useEffect(() => {
    if (showAuditHistory && !auditHistory.length) {
      loadAuditHistory();
    }
  }, [showAuditHistory]);

  // Load courier tracking when expanded
  useEffect(() => {
    if (showCourierTracking && !courierTracking && order?.airwaybillNumber) {
      loadCourierTracking();
    }
  }, [showCourierTracking, order]);

  const handleConfirmReverseOrder = async (targetStatusKey, orderStatusReasonKey) => {
    setIsReversing(true);
    try {
      const result = await reverseSingleOrder(orderId, targetStatusKey, orderStatusReasonKey);
      setShowReverseDialog(false);
      const targetLabel = targetStatusKey === 'cancelled' ? 'Cancelled' : 'Confirmed';
      success(`Order ${result.orderNumber} reversed successfully and returned to ${targetLabel} status.`);
      // Reload the order details
      loadOrderDetails(); // or whatever your load function is named
    } catch (error) {
      console.error('Reverse order error:', error);
      showError(error.response?.data?.message || 'Failed to reverse order');
    } finally {
      setIsReversing(false);
    }
  };

  const loadOrderDetails = async () => {
    try {
      setIsLoadingOrder(true);
      setOrderError(null);
      const response = await getEcomOrder(orderId);
      setOrder(response.order);
    } catch (err) {
      console.error('Failed to load order:', err);
      setOrderError(err.response?.data?.message || t('ecomOrders.failedToLoadOrder'));
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const loadAuditSummary = async () => {
    try {
      const response = await getOrderAuditSummary(orderId);
      setAuditSummary(response.summary);
    } catch (err) {
      console.error('Failed to load audit summary:', err);
    }
  };

  const loadAuditHistory = async (page = 1) => {
    try {
      setIsLoadingAudit(true);
      setAuditError(null);
      const response = await getOrderAuditHistory(orderId, {
        page,
        limit: auditLimit
      });
      setAuditHistory(response.audits);
      setAuditTotal(response.total);
      setAuditPage(page);
    } catch (err) {
      console.error('Failed to load audit history:', err);
      setAuditError(t('ecomOrders.failedToLoadAuditHistory'));
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const loadCourierTracking = async () => {
    try {
      setIsLoadingTracking(true);
      setTrackingError(null);
      const response = await getOrderCourierTracking(orderId);
      setCourierTracking(response.data);
    } catch (err) {
      console.error('Failed to load courier tracking:', err);
      setTrackingError(err.response?.data?.message || t('ecomOrders.failedToLoadCourierTracking'));
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const loadOrderRelations = async () => {
    try {
      setIsLoadingRelations(true);
      setRelationsError(null);
      const response = await getOrderRelations(orderId);
      setOrderRelations(response.relations);
    } catch (err) {
      console.error('Failed to load order relations:', err);
      setRelationsError(err.response?.data?.message || t('ecomOrders.failedToLoadRelations'));
    } finally {
      setIsLoadingRelations(false);
    }
  };

  const getOrderStatusIcon = (statusKey) => {
    const statusIcons = {
      pending: <Clock size={16} />,
      confirmed: <CheckCircle size={16} />,
      hold: <Clock size={16} />,
      not_confirmed: <XCircle size={16} />,
      cancelled: <XCircle size={16} />,
      in_courier: <Truck size={16} />,
      settled: <CheckCircle size={16} />,
      returned: <XCircle size={16} />
    };
    return statusIcons[statusKey] || <Clock size={16} />;
  };

  const getAuditActionIcon = (action) => {
    const actionIcons = {
      CREATE: <CheckCircle size={14} />,
      UPDATE: <Edit size={14} />,
      DELETE: <XCircle size={14} />,
      STATUS_CHANGE: <RefreshCw size={14} />
    };
    return actionIcons[action] || <Activity size={14} />;
  };

  const getAuditActionColor = (action) => {
    const actionColors = {
      CREATE: '#059669',
      UPDATE: '#3b82f6',
      DELETE: '#dc2626',
      STATUS_CHANGE: '#f59e0b'
    };
    return actionColors[action] || '#6b7280';
  };

  // Convert camelCase to Title Case with spaces
  const camelCaseToTitle = (str) => {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  };

  // Format audit change value for display
  const formatChangeValue = (value) => {
    if (value === null || value === undefined) {
      return 'Empty';
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Render changes for CREATE action with nested object
  const renderCreateChanges = (changes) => {
    if (!changes.created || !changes.created.to) {
      return null;
    }

    const createdData = changes.created.to;

    return (
      <>
        <div className={styles.auditChanges}>
          {Object.entries(createdData).map(([field, value]) => (
            <div key={field} className={styles.auditChange}>
              <span className={styles.changeField}>{camelCaseToTitle(field)}</span>
              <div className={styles.changeValues}>
                <span className={styles.oldValue}>Empty</span>
                <span className={styles.changeArrow}>→</span>
                <span className={styles.newValue}>{formatChangeValue(value)}</span>
              </div>
            </div>
          ))}
        </div>
        {renderItemChanges(changes.items)}
      </>
    );
  };

  const renderChangeRows = (rows) => (
    <div className={styles.auditChanges}>
      {rows.map(({ field, from, to }) => (
        <div key={field} className={styles.auditChange}>
          <span className={styles.changeField}>{camelCaseToTitle(field)}</span>
          <div className={styles.changeValues}>
            <span className={styles.oldValue}>{formatChangeValue(from)}</span>
            <span className={styles.changeArrow}>→</span>
            <span className={styles.newValue}>{formatChangeValue(to)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  // `items` is `{ added, removed, updated }` per product line
  const renderItemChanges = (items) => {
    if (!items || typeof items !== 'object') return null;
    return AUDIT_ITEM_GROUPS.flatMap(({ key, labelKey }) => (items[key] || []).map((item, index) => {
      const rows = key === 'updated'
        ? Object.entries(item.changes || {}).map(([field, value]) => ({ field, from: value.from ?? null, to: value.to ?? null }))
        : AUDIT_ITEM_FIELDS
          .filter((field) => item[field] !== null && item[field] !== undefined)
          .map((field) => (key === 'added' ? { field, from: null, to: item[field] } : { field, from: item[field], to: null }));
      return (
        <div key={`${key}-${index}`} className={styles.auditItemChange}>
          <div className={styles.auditItemHeader}>
            <strong>{item.productName || t('common.na')}</strong>
            {item.sku && <span className={styles.auditItemSku}>{item.sku}</span>}
            {item.bundleName && (
              <span className={styles.auditPaymentRecordTag}>{t('ecomOrders.auditBundleTag', { name: item.bundleName })}</span>
            )}
            <span className={styles.auditPaymentRecordTag}>{t(labelKey)}</span>
          </div>
          {renderChangeRows(rows)}
        </div>
      );
    }));
  };

  const CROSS_SELL_AUDIT_FIELDS = [
    { key: 'productName', label: 'Product' },
    { key: 'crossSellQuantity', label: 'Cross-Sell Quantity' },
    { key: 'crossSellByName', label: 'Cross-Sold By' },
    { key: 'baselineQuantity', label: 'Baseline Quantity' },
    { key: 'createdAt', label: 'Cross-Sell Date' },
  ];

  const getCrossSellSnapshotValue = (snapshot, key) => {
    if (!snapshot) return null;
    if (key === 'crossSellByName') return snapshot.crossSellBy?.name || null;
    if (key === 'createdAt') return snapshot.createdAt ? formatDateTime(snapshot.createdAt) : null;
    return snapshot[key];
  };

  const CROSS_SELL_DIFF_LABELS = {
    productName: 'Product',
    crossSellQuantity: 'Cross-Sell Quantity',
    crossSellByName: 'Cross-Sold By',
    baselineQuantity: 'Baseline Quantity',
  };

  const renderCrossSellChange = (crossSell) => {
    const { from, to, changes } = crossSell || {};

    if (changes) {
      return (
        <div className={styles.auditChanges}>
          <div className={styles.auditChange}>
            <span className={styles.changeField}>Product</span>
            <div className={styles.changeValues}>
              <span className={styles.newValue}>{formatChangeValue(crossSell.productName)}</span>
            </div>
          </div>
          {Object.entries(changes).map(([field, change]) => (
            <div key={field} className={styles.auditChange}>
              <span className={styles.changeField}>{CROSS_SELL_DIFF_LABELS[field] || camelCaseToTitle(field)}</span>
              <div className={styles.changeValues}>
                <span className={styles.oldValue}>{formatChangeValue(change.from)}</span>
                <span className={styles.changeArrow}>→</span>
                <span className={styles.newValue}>{formatChangeValue(change.to)}</span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.auditChanges}>
        {CROSS_SELL_AUDIT_FIELDS.map(({ key, label }) => (
          <div key={key} className={styles.auditChange}>
            <span className={styles.changeField}>{label}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatChangeValue(getCrossSellSnapshotValue(from, key))}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatChangeValue(getCrossSellSnapshotValue(to, key))}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const MESSAGE_CENTER_SHIFT_AUDIT_FIELDS = [
    { key: 'shiftDate', label: 'Shift Date' },
    { key: 'shiftTypeName', label: 'Shift Type' },
  ];

  const renderMessageCenterShiftChange = (shiftChange) => {
    const { from, to } = shiftChange || {};
    return (
      <div className={styles.auditChanges}>
        {MESSAGE_CENTER_SHIFT_AUDIT_FIELDS.map(({ key, label }) => (
          <div key={key} className={styles.auditChange}>
            <span className={styles.changeField}>{label}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatChangeValue(from ? from[key] : null)}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatChangeValue(to ? to[key] : null)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render changes for UPDATE action
  const renderUpdateChanges = (changes) => {
    return (
      <>
        {renderChangeRows(
          Object.entries(changes)
            .filter(([field]) => field !== 'items')
            .map(([field, change]) => ({ field, from: change.from, to: change.to }))
        )}
        {renderItemChanges(changes.items)}
      </>
    );
  };

  const PAYMENT_RECORD_AUDIT_FIELDS = [
    { key: 'paymentMethodLabel', label: 'Payment Method' },
    { key: 'paymentAmount', label: 'Amount' },
    { key: 'reference', label: 'Reference' },
    { key: 'paymentDate', label: 'Payment Date' },
    { key: 'proofUrl', label: 'Proof File' },
    { key: 'isVerified', label: 'Verified' },
    { key: 'verifiedByName', label: 'Verified By' },
    { key: 'verifiedAt', label: 'Verified At' },
  ];

  const REPLACEMENT_DETAIL_AUDIT_FIELDS = [
    { key: 'replacedOrderNumber', label: 'Replaced Order' },
    { key: 'replacementReasonLabel', label: 'Replacement Reason' },
    { key: 'returnAirwaybillNumber', label: 'Return Airwaybill Number' },
  ];

  // Render changes for a ReplacementOrder create/update audit entry
  const renderReplacementDetailChange = (replacementDetailChange) => {
    const { from, to } = replacementDetailChange;
    const rows = REPLACEMENT_DETAIL_AUDIT_FIELDS
      .map(({ key, label }) => ({ label, from: from?.[key], to: to?.[key] }))
      .filter(({ from: f, to: t }) => (f ?? null) !== (t ?? null));

    if (rows.length === 0) return null;

    return (
      <div className={styles.auditChanges}>
        {rows.map(({ label, from: f, to: t }) => (
          <div key={label} className={styles.auditChange}>
            <span className={styles.changeField}>{label}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatChangeValue(f)}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatChangeValue(t)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render changes for a PaymentRecord create/update/delete audit entry
  const renderPaymentRecordChange = (paymentRecordChange) => {
    const { from, to } = paymentRecordChange;
    const rows = PAYMENT_RECORD_AUDIT_FIELDS
      .map(({ key, label }) => ({ label, from: from?.[key], to: to?.[key] }))
      .filter(({ from: f, to: t }) => (f ?? null) !== (t ?? null));

    if (rows.length === 0) return null;

    return (
      <div className={styles.auditChanges}>
        {rows.map(({ label, from: f, to: t }) => (
          <div key={label} className={styles.auditChange}>
            <span className={styles.changeField}>{label}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatChangeValue(f)}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatChangeValue(t)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoadingOrder) {
    return (
      <div className={styles.orderDetailsPage}>
        <div className={styles.loadingState}>
          <Loader2 className={styles.loadingSpinnerLarge} />
          <h3>{t('common.loading')}</h3>
          <p>{t('ecomOrders.loadingOrder')}</p>
        </div>
      </div>
    );
  }

  if (orderError) {
    return (
      <div className={styles.orderDetailsPage}>
        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('common.error')}</h3>
          <p>{orderError}</p>
          <button onClick={() => navigate('/orders')} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('ecomOrders.backToOrders')}
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const primaryPhone = order.phoneNumbers?.find(p => p.isPrimary) || order.phoneNumbers?.[0];

  const hasRelations = (orderRelations && (
    orderRelations.packingBatches?.length > 0 ||
    orderRelations.returnBatches?.length > 0 ||
    orderRelations.courierInvoices?.length > 0
  )) || !!order.replacementDetail?.returnPackageOrder;

  const renderItemRow = (item, key, rowClass) => (
    <tr key={key} className={rowClass}>
      <td>
        <div className={styles.productInfo}>
          <span className={styles.productName}>{item.Product?.name || 'N/A'}</span>
          {item.ProductVariation?.sku && (
            <span className={styles.productSku}>SKU: {item.ProductVariation.sku}</span>
          )}
        </div>
      </td>
      <td className={styles.textRight}>{item.quantity}</td>
      <td className={styles.textRight}>{formatCurrency(item.unitPrice)}</td>
      <td className={styles.textRight}>
        {item.lineDiscount > 0 ? formatCurrency(item.lineDiscount) : '-'}
      </td>
      <td className={styles.textRight}>
        <strong>{formatCurrency(item.lineTotal)}</strong>
      </td>
    </tr>
  );

  return (
    <div className={styles.orderDetailsPage}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Package className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('ecomOrders.orderDetails')}</h1>
              <p className={styles.orderNumber}>#{order.orderNumber}</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button 
              onClick={() => navigate('/orders')} 
              className={styles.secondaryButton}
            >
              <ArrowLeft size={20} />
              {t('ecomOrders.backToOrders')}
            </button>
            {(hasPermission('edit_in_courier_order_payments') || hasPermission('edit_in_courier_order_shift')) && order?.OrderStatus?.key === 'in_courier' && (
              <button
                onClick={() => setShowAdjustModal(true)}
                className={styles.secondaryButton}
              >
                <Wallet size={20} />
                {t('ecomOrders.editPaymentsAndShift')}
              </button>
            )}
            {hasPermission('edit_ecom_order') && (
              <button
                onClick={() => navigate(`/orders/${orderId}/edit`)}
                className={styles.primaryButton}
              >
                <Edit size={20} />
                {t('common.edit')}
              </button>
            )}
            {order?.shareToken && (
              <button
                className={styles.secondaryButton}
                onClick={() => {
                  const url = `${window.location.origin}/invoice/${order.shareToken}`;
                  navigator.clipboard.writeText(url).then(() => success('Invoice link copied to clipboard!')).catch(() => showError('Could not copy link'));
                }}
                title="Copy shareable invoice link"
              >
                <Share2 size={18} />
                Share Invoice
              </button>
            )}
            {canReverse && order && (order.OrderStatus?.isInPacking || order.OrderStatus?.isDispatched) && (
              <button
                className={`${styles.secondaryButton} ${styles.reverseButton}`}
                onClick={() => setShowReverseDialog(true)}
                disabled={isReversing}
              >
                <RotateCcw size={18} />
                Reverse Order
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.pageContent}>
        <div className={styles.mainColumn}>
          {/* Order Status Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <Activity size={20} />
                <h2>{t('ecomOrders.orderStatus')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem}>
                  <label>{t('ecomOrders.currentStatus')}</label>
                  <div 
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: order.OrderStatus?.colorCode 
                        ? `${order.OrderStatus.colorCode}20` 
                        : '#F59E0B20',
                      borderColor: order.OrderStatus?.colorCode || '#F59E0B',
                      color: order.OrderStatus?.colorCode || '#92400e'
                    }}
                  >
                    {getOrderStatusIcon(order.OrderStatus?.key)}
                    <span>{order.OrderStatus?.label || order.orderStatusKey}</span>
                  </div>
                </div>
                {order.OrderStatusReason && (
                  <div className={styles.statusItem}>
                    <label>{t('ecomOrders.statusReason')}</label>
                    <p>{order.OrderStatusReason.label}</p>
                  </div>
                )}
                {order.holdUntilDate && (
                  <div className={styles.statusItem}>
                    <label>{t('ecomOrders.holdUntilDate')}</label>
                    <p>
                      <Calendar size={14} />
                      {formatDate(order.holdUntilDate)}
                    </p>
                  </div>
                )}
                <div className={styles.statusItem}>
                  <label>{t('ecomOrders.paymentType')}</label>
                  <div className={styles.paymentTypeBadge}>
                    <CreditCard size={14} />
                    {order.PaymentType?.label || order.paymentTypeKey}
                  </div>
                </div>
                {order.callAttempts > 0 && (
                  <div className={styles.statusItem}>
                    <label>{t('ecomOrders.callAttempts')}</label>
                    <p>{order.callAttempts}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <User size={20} />
                <h2>{t('ecomOrders.customerInformation')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.customerName')}</label>
                  <p className={styles.customerName}>
                    <User size={14} />
                    {order.customerName}
                  </p>
                </div>
                {order.customerEmail && (
                  <div className={styles.infoItem}>
                    <label>{t('ecomOrders.customerEmail')}</label>
                    <a href={`mailto:${order.customerEmail}`} className={styles.emailLink}>
                      <Mail size={14} />
                      {order.customerEmail}
                    </a>
                  </div>
                )}
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.phoneNumbers')}</label>
                  <div className={styles.phoneList}>
                    {order.phoneNumbers?.map((phone, idx) => (
                      <a 
                        key={idx}
                        href={`tel:${phone.phoneNumber}`}
                        className={`${styles.phoneLink} ${phone.isPrimary ? styles.primaryPhone : ''}`}
                      >
                        <Phone size={14} />
                        {phone.phoneNumber}
                        {phone.isPrimary && <span className={styles.primaryBadge}>Primary</span>}
                      </a>
                    ))}
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.address')}</label>
                  <p className={styles.addressText}>
                    <MapPin size={14} />
                    {order.address}
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>{t('ecomOrders.city')}</label>
                  <p>{order.City?.name || 'N/A'}</p>
                </div>
                {order.privateReference && (
                  <div className={styles.infoItem}>
                    <label>{t('ecomOrders.privateReference')}</label>
                    <p>{order.privateReference}</p>
                  </div>
                )}
                {order.deliveryNotes && (
                  <div className={styles.infoItem}>
                    <label>{t('ecomOrders.deliveryNotes')}</label>
                    <p className={styles.notesText}>{order.deliveryNotes}</p>
                  </div>
                )}
                {order.specialNotes && (
                  <div className={styles.infoItem}>
                    <label>{t('ecomOrders.specialNotes')}</label>
                    <p className={styles.notesText}>{order.specialNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <Package size={20} />
                <h2>{t('ecomOrders.orderItems')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.itemsTable}>
                <table>
                  <thead>
                    <tr>
                      <th>{t('ecomOrders.product')}</th>
                      <th className={styles.textRight}>{t('ecomOrders.quantity')}</th>
                      <th className={styles.textRight}>{t('ecomOrders.unitPrice')}</th>
                      <th className={styles.textRight}>{t('ecomOrders.lineDiscount')}</th>
                      <th className={styles.textRight}>{t('ecomOrders.lineTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupOrderItems(order.orderItems || []).map((entry, gIdx) => (entry.type === 'bundle' ? (
                    <BundleItemGroup
                      key={entry.groupKey}
                      group={entry}
                      renderItem={(item, idx, rowClass) => renderItemRow(item, `${entry.groupKey}-${idx}`, rowClass)}
                    />
                  ) : renderItemRow(entry.item, gIdx)))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Related Documents */}
          {hasRelations && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <FileText size={20} />
                  <h2>{t('ecomOrders.relatedDocuments')}</h2>
                </div>
              </div>
              <div className={styles.cardContent}>
                {isLoadingRelations ? (
                  <div className={styles.relationsLoading}>
                    <Loader2 className={styles.loadingSpinner} />
                    <span>{t('common.loading')}</span>
                  </div>
                ) : relationsError ? (
                  <div className={styles.relationsError}>
                    <AlertCircle size={16} />
                    <span>{relationsError}</span>
                  </div>
                ) : (
                  <div className={styles.relationsGrid}>
                    {orderRelations.packingBatches?.length > 0 && (
                      <div className={styles.relationsSection}>
                        <div className={styles.relationsSectionHeader}>
                          <Box size={18} />
                          <h3>{t('ecomOrders.packingBatches')}</h3>
                        </div>
                        <div className={styles.relationsList}>
                          {orderRelations.packingBatches.map((batch) => (
                            <a
                              key={batch.id}
                              href={`/packing-batches/${batch.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.relationLink}
                            >
                              <span>{batch.batchNumber}</span>
                              <ExternalLink size={14} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {orderRelations.returnBatches?.length > 0 && (
                      <div className={styles.relationsSection}>
                        <div className={styles.relationsSectionHeader}>
                          <RotateCcw size={18} />
                          <h3>{t('ecomOrders.returnBatches')}</h3>
                        </div>
                        <div className={styles.relationsList}>
                          {orderRelations.returnBatches.map((batch) => (
                            <a
                              key={batch.id}
                              href={`/return-batches/${batch.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.relationLink}
                            >
                              <span>{batch.batchNumber}</span>
                              <ExternalLink size={14} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {orderRelations.courierInvoices?.length > 0 && (
                      <div className={styles.relationsSection}>
                        <div className={styles.relationsSectionHeader}>
                          <Receipt size={18} />
                          <h3>{t('ecomOrders.courierInvoices')}</h3>
                        </div>
                        <div className={styles.relationsList}>
                          {orderRelations.courierInvoices.map((invoice) => (
                            <a
                              key={invoice.id}
                              href={`/courier-invoices/${invoice.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.relationLink}
                            >
                              <span>{invoice.invoiceNumber}</span>
                              <ExternalLink size={14} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {order.replacementDetail?.returnPackageOrder && (
                      <div className={styles.relationsSection}>
                        <div className={styles.relationsSectionHeader}>
                          <RotateCcw size={18} />
                          <h3>{t('ecomOrders.returnPackageOrder')}</h3>
                        </div>
                        <div className={styles.relationsList}>
                          <a
                            href={`/orders/${order.replacementDetail.returnPackageOrder.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.relationLink}
                          >
                            <span>{order.replacementDetail.returnPackageOrder.orderNumber}</span>
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Courier Information */}
          {(order.airwaybillNumber || order.assignedCourierAccount || order.courierStatus) && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <Truck size={20} />
                  <h2>{t('ecomOrders.courierInformation')}</h2>
                </div>
                {order.airwaybillNumber && (
                  <button
                    onClick={() => setShowCourierTracking(!showCourierTracking)}
                    className={styles.toggleButton}
                  >
                    {showCourierTracking ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {t('ecomOrders.trackingDetails')}
                  </button>
                )}
              </div>
              <div className={styles.cardContent}>
                <div className={styles.infoGrid}>
                  {order.airwaybillNumber && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.airwaybillNumber')}</label>
                      <p className={styles.airwaybillText}>{order.airwaybillNumber}</p>
                    </div>
                  )}
                  {order.assignedCourierAccount && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.courierName')}</label>
                      <p>{order.assignedCourierAccount.accountName}</p>
                    </div>
                  )}
                  {order.courierStatus && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.courierStatus')}</label>
                      <p>{order.courierStatus.statusLabel}</p>
                    </div>
                  )}
                  {order.courierStatusUpdatedAt && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.courierStatusUpdatedAt')}</label>
                      <p>
                        <Calendar size={14} />
                        {formatDateTime(order.courierStatusUpdatedAt)}
                      </p>
                    </div>
                  )}
                  {order.dispatchedDate && (
                    <div className={styles.infoItem}>
                      <label>{t('ecomOrders.dispatchedDate')}</label>
                      <p>
                        <Calendar size={14} />
                        {formatDate(order.dispatchedDate)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Courier Tracking Details */}
                {showCourierTracking && (
                  <div className={styles.trackingSection}>
                    {order.deliveryNotes?.trim() && (
                      <div className={styles.clientRemarks}>
                        <h4>{t('ecomOrders.clientRemarks')}</h4>
                        <p className={styles.clientRemarksText}>{order.deliveryNotes}</p>
                      </div>
                    )}
                    {isLoadingTracking ? (
                      <div className={styles.trackingLoading}>
                        <Loader2 className={styles.loadingSpinner} />
                        <span>{t('ecomOrders.loadingTracking')}</span>
                      </div>
                    ) : trackingError ? (
                      <div className={styles.trackingError}>
                        <AlertCircle size={16} />
                        <span>{trackingError}</span>
                      </div>
                    ) : courierTracking ? (
                      <>
                        {courierTracking.packageTimeline && courierTracking.packageTimeline.length > 0 && (
                          <div className={styles.timeline}>
                            <h4>{t('ecomOrders.packageTimeline')}</h4>
                            <div className={styles.timelineList}>
                              {courierTracking.packageTimeline.map((event, idx) => (
                                <div key={idx} className={styles.timelineEvent}>
                                  <div className={styles.timelineMarker}></div>
                                  <div className={styles.timelineContent}>
                                    <div className={styles.timelineDate}>
                                      {event.date ? event.date : 'N/A'}
                                    </div>
                                    <div className={styles.timelineNote}>{event.note}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit History */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <History size={20} />
                <h2>{t('ecomOrders.auditHistory')}</h2>
              </div>
              <button
                onClick={() => setShowAuditHistory(!showAuditHistory)}
                className={styles.toggleButton}
              >
                {showAuditHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showAuditHistory ? t('common.hide') : t('common.show')}
              </button>
            </div>
            
            {auditSummary && (
              <div className={styles.auditSummaryBar}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('ecomOrders.totalChanges')}</span>
                  <span className={styles.summaryValue}>{auditSummary.totalChanges || 0}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('ecomOrders.lastModified')}</span>
                  <span className={styles.summaryValue}>
                    {order.updatedAt ? formatDateTime(order.updatedAt) : 'N/A'}
                  </span>
                </div>
              </div>
            )}

            {showAuditHistory && (
              <div className={styles.cardContent}>
                {isLoadingAudit ? (
                  <div className={styles.auditLoading}>
                    <Loader2 className={styles.loadingSpinner} />
                    <span>{t('common.loading')}</span>
                  </div>
                ) : auditError ? (
                  <div className={styles.auditError}>
                    <AlertCircle size={16} />
                    <span>{auditError}</span>
                  </div>
                ) : auditHistory.length > 0 ? (
                  <>
                    <div className={styles.auditList}>
                      {auditHistory.map((audit, idx) => (
                        <div key={audit.id} className={styles.auditItem}>
                          <div className={styles.auditHeader}>
                            <div className={styles.auditAction}>
                              <div 
                                className={styles.auditActionBadge}
                                style={{ 
                                  backgroundColor: `${getAuditActionColor(audit.action)}20`,
                                  color: getAuditActionColor(audit.action)
                                }}
                              >
                                {getAuditActionIcon(audit.action)}
                                <span>{audit.action}</span>
                              </div>
                              {audit.changes?.paymentRecord && (
                                <span className={styles.auditPaymentRecordTag}>
                                  {t('ecomOrders.paymentRecordChange') || 'Payment Record'}
                                </span>
                              )}
                              <span className={styles.auditUser}>
                                by {audit.changedBy?.name || 'Unknown'}
                              </span>
                              {audit.changes?.crossSell && (
                                <span className={styles.auditActionBadge}>Cross-Sell</span>
                              )}
                              {audit.changes?.messageCenterShift && (
                                <span className={styles.auditActionBadge}>Message Center Shift</span>
                              )}
                            </div>
                            <div className={styles.auditDate}>
                              <Clock size={12} />
                              {formatDateTime(audit.createdAt)}
                            </div>
                          </div>

                          {audit.changes && Object.keys(audit.changes).length > 0 && (
                            audit.changes.crossSell
                              ? renderCrossSellChange(audit.changes.crossSell)
                              : audit.changes.messageCenterShift
                              ? renderMessageCenterShiftChange(audit.changes.messageCenterShift)
                              : audit.changes.paymentRecord
                              ? renderPaymentRecordChange(audit.changes.paymentRecord)
                              : audit.changes.replacementDetail
                              ? renderReplacementDetailChange(audit.changes.replacementDetail)
                              : audit.action === 'CREATE' && audit.changes.created
                              ? renderCreateChanges(audit.changes)
                              : renderUpdateChanges(audit.changes)
                          )}

                          {audit.changeReason && (
                            <div className={styles.auditReason}>
                              <FileText size={12} />
                              {audit.changeReason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {auditTotal > auditLimit && (
                      <div className={styles.pagination}>
                        <button
                          onClick={() => loadAuditHistory(auditPage - 1)}
                          disabled={auditPage === 1}
                          className={styles.paginationButton}
                        >
                          Previous
                        </button>
                        <span className={styles.paginationInfo}>
                          Page {auditPage} of {Math.ceil(auditTotal / auditLimit)}
                        </span>
                        <button
                          onClick={() => loadAuditHistory(auditPage + 1)}
                          disabled={auditPage >= Math.ceil(auditTotal / auditLimit)}
                          className={styles.paginationButton}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <History size={32} />
                    <p>{t('ecomOrders.noAuditHistory')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>
          {/* Order Summary */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <DollarSign size={20} />
                <h2>{t('ecomOrders.orderSummary')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryRow}>
                  <span>{t('ecomOrders.subtotal')}</span>
                  <span>{formatCurrency(order.totalAmount)}</span>
                </div>
                {order.totalDiscount > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('ecomOrders.totalDiscount')}</span>
                    <span className={styles.discountAmount}>-{formatCurrency(order.totalDiscount)}</span>
                  </div>
                )}
                <div className={styles.summaryRow}>
                  <span>{t('ecomOrders.deliveryCharge')}</span>
                  <span>{formatCurrency(order.deliveryCharge)}</span>
                </div>
                {order.totalTax > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('ecomOrders.tax')}</span>
                    <span>{formatCurrency(order.totalTax)}</span>
                  </div>
                )}
                <div className={`${styles.summaryRow} ${styles.grandTotalRow}`}>
                  <span>{t('ecomOrders.grandTotal')}</span>
                  <span>{formatCurrency(order.grandTotal)}</span>
                </div>
                {order.paidAmount > 0 && (
                  <>
                    <div className={styles.summaryDivider}></div>
                    <div className={styles.summaryRow}>
                      <span>{t('ecomOrders.paidAmount')}</span>
                      <span className={styles.paidAmount}>{formatCurrency(order.paidAmount)}</span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span>{t('ecomOrders.codAmount')}</span>
                      <span className={styles.codAmount}>{formatCurrency(order.codAmount)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Payment Records */}
          {order.paymentRecords && order.paymentRecords.length > 0 && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <CreditCard size={20} />
                  <h2>{t('ecomOrders.paymentRecords')}</h2>
                </div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.paymentList}>
                  {order.paymentRecords.map((payment, idx) => (
                    <div key={idx} className={styles.paymentRecord}>
                      {payment.isVerified && (
                        <span className={styles.verifiedRecordBadge} title={t('ecomOrders.verifiedPaymentRecord') || 'Verified'}>
                          <CheckCircle2 size={18} />
                        </span>
                      )}
                      <div className={styles.paymentHeader}>
                        <PaymentMethodIcon
                          iconFileName={payment.PaymentMethod?.iconFileName}
                          label={payment.PaymentMethod?.label}
                          className={styles.paymentMethod}
                        />
                        <span className={styles.paymentAmount}>
                          {formatCurrency(payment.paymentAmount)}
                        </span>
                      </div>
                      {payment.reference && (
                        <div className={styles.paymentReference}>
                          Ref: {payment.reference}
                        </div>
                      )}
                      <div className={styles.paymentDate}>
                        <Calendar size={12} />
                        {formatDate(payment.paymentDate)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FileText size={20} />
                <h2>{t('ecomOrders.additionalInformation')}</h2>
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.infoList}>
                <div className={styles.infoListItem}>
                  <label>{t('ecomOrders.shopName')}</label>
                  <p>
                    <Store size={14} />
                    {order.Shop?.name || 'N/A'}
                  </p>
                </div>
                {order.OrderOrigin && (
                  <div className={styles.infoListItem}>
                    <label>{t('ecomOrders.orderOrigin')}</label>
                    <OrderOriginBadge origin={order.OrderOrigin} />
                  </div>
                )}
                {order.shiftMap?.shift && (
                  <div className={styles.infoListItem}>
                    <label>{t('ecomOrders.messageCenterShift')}</label>
                    <p><OrderShiftLink shiftMap={order.shiftMap} /></p>
                  </div>
                )}
                <div className={styles.infoListItem}>
                  <label>{t('ecomOrders.createdBy')}</label>
                  <p>
                    <User size={14} />
                    {order.createdBy?.name || 'N/A'}
                  </p>
                </div>
                <div className={styles.infoListItem}>
                  <label>{t('ecomOrders.createdAt')}</label>
                  <p>
                    <Calendar size={14} />
                    {formatDateTime(order.createdAt)}
                  </p>
                </div>
                {order.confirmedByUser && (
                  <div className={styles.infoListItem}>
                    <label>{t('ecomOrders.confirmedBy')}</label>
                    <p>
                      <User size={14} />
                      {order.confirmedByUser.name}
                    </p>
                  </div>
                )}
                {order.confirmedAt && (
                  <div className={styles.infoListItem}>
                    <label>{t('ecomOrders.confirmedAt')}</label>
                    <p>
                      <Calendar size={14} />
                      {formatDateTime(order.confirmedAt)}
                    </p>
                  </div>
                )}
                {order.updatedAt && order.updatedAt !== order.createdAt && (
                  <div className={styles.infoListItem}>
                    <label>{t('ecomOrders.updatedAt')}</label>
                    <p>
                      <Calendar size={14} />
                      {formatDateTime(order.updatedAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showAdjustModal && order && (
        <EcomOrderPaymentRecordsModal
          order={order}
          canEditPayments={hasPermission('edit_in_courier_order_payments')}
          canEditShift={hasPermission('edit_in_courier_order_shift')}
          onClose={() => setShowAdjustModal(false)}
          onSaved={() => {
            loadOrderDetails();
            setAuditHistory([]);
            if (showAuditHistory) loadAuditHistory();
          }}
        />
      )}

      <ReversalConfirmDialog
        isOpen={showReverseDialog}
        onClose={() => setShowReverseDialog(false)}
        onConfirm={handleConfirmReverseOrder}
        title={`Reverse Order #${order?.orderNumber || ''}`}
        description={`This order will be removed from its packing batch, its airwaybill number cleared${order?.OrderStatus?.isDispatched ? ', and stock movements reversed' : ''}.`}
        isLoading={isReversing}
      />
    </div>
  );
};

export default OrderDetailsPage;